import { STRUCTURES, UNSLEEPING, type StructureType } from '@somnhold/content';
import { cellAtWorld, cellHex } from '../../grid.ts';
import { hexToWorld } from '../../hex.ts';
import { ANGLE_STEPS, iatan2, imax, mulBp, withinRange } from '../../math/fixed.ts';
import { isInside } from '../../walls.ts';
import type { BattleState, Enemy, Hero, Structure, UnsleepingState, Zone } from '../../types.ts';
import { damageHero, damageStructure, ev } from '../combat.ts';
import { firesAsTower } from '../towers.ts';

/*
 * What every Unsleeping's kit is made of (docs/ANTIHEROES.md): the shape of a kit the core runs (battle/unsleeping.ts
 * owns the rage, the channel and its cut, the phases, the flight), and the pieces kits share.
 */

/** One Unsleeping's own powers. The core calls them; `u` is his state, `e` his body. */
export interface Kit {
  /** His opening cooldowns and counters (`u.cds`, `u.k`). */
  init(s: BattleState, e: Enemy, u: UnsleepingState): void;
  /** Each tick an action of his runs (`u.busy` > 0): a charge, a rain, a whirlwind. True while it holds him. */
  busy?(s: BattleState, e: Enemy, u: UnsleepingState): boolean;
  /** Each tick he is free (not stunned, channelling, busy or fading): his skills. True when one moved or held him. */
  act(s: BattleState, e: Enemy, u: UnsleepingState): boolean;
  /** His rage is full: starts the channel's aim (`u.castA`, `u.spots`, `u.tgts`) and says so. False with nothing worth it. */
  aim(s: BattleState, e: Enemy, u: UnsleepingState): boolean;
  /** The channel ran out uncut: the ultimate lands. */
  land(s: BattleState, e: Enemy, u: UnsleepingState): void;
  /** His passive guard: what a blow from (x, y) comes to before the core's exposure and last phase (no point: from nowhere). */
  takes?(s: BattleState, e: Enemy, u: UnsleepingState, dmg: number, x?: number, y?: number): number;
  /** His basic blow on `h` (`dmg` already his scaled damage). */
  strike(s: BattleState, e: Enemy, h: Hero, dmg: number): void;
  /** He just crossed into phase `e.phase`. */
  phase?(s: BattleState, e: Enemy, u: UnsleepingState): void;
  /** Every tick, stunned or not (passives, orbs, timers). */
  status?(s: BattleState, e: Enemy, u: UnsleepingState): void;
  /** Another nightmare fell while he stands (souls). */
  died?(s: BattleState, e: Enemy, u: UnsleepingState, dead: Enemy): void;
  /** A blow on another nightmare while he stands (an oath over it): what it comes to; `pid` the hero's ('' a tower's). */
  guards?(s: BattleState, e: Enemy, u: UnsleepingState, target: Enemy, dmg: number, pid: string): number;
  /** Nothing lands on him while this holds. */
  untouchable?(e: Enemy, u: UnsleepingState): boolean;
}

export const standing = (h: Hero): boolean => h.downTicks === 0;

/** The angle from `a` to `b` folded into (-half a turn, half a turn]. */
export const turn = (a: number, b: number): number => ((((b - a) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS) - ANGLE_STEPS / 2;

/** Within a cone from `e`: closer than `r` and within `half` of angle `a`. */
export const inCone = (e: Enemy, a: number, x: number, y: number, r: number, half: number): boolean => {
  if (!withinRange(e.x, e.y, x, y, r)) return false;
  if (x === e.x && y === e.y) return true;
  const d = turn(a, iatan2(y - e.y, x - e.x));
  return d <= half && d >= -half;
};

const insideAt = (s: BattleState, x: number, y: number): boolean => {
  const c = cellAtWorld(s, x, y);
  if (c < 0) return false;
  const h = cellHex(s.map.radius, c);
  return isInside(s.map, h.q, h.r);
};

/** A hero he can walk to: on his side of the walls. */
export const reachable = (s: BattleState, e: Enemy, h: Hero): boolean => insideAt(s, e.x, e.y) === insideAt(s, h.x, h.y);

/** A blow, a skill: never less than `maxHpBp` of the hero's own health. */
export const by = (h: Hero, dmg: number, maxHpBp: number): number => imax(dmg, mulBp(h.stats.maxHp, maxHpBp));

/** His power on a hero: `dmg` scaled by his strength, at least `maxHpBp` of its health, through armour and shields. */
export const hurt = (s: BattleState, e: Enemy, u: UnsleepingState, h: Hero, dmg: number, maxHpBp: number, kind?: Parameters<typeof damageHero>[4]): void => {
  damageHero(s, h, by(h, mulBp(dmg, u.dmgBp), maxHpBp), e, kind, true);
};

/** Standing heroes within `r` of (x, y). */
export const heroesNear = (s: BattleState, x: number, y: number, r: number): Hero[] => s.heroes.filter((h) => standing(h) && withinRange(x, y, h.x, h.y, r));

/** A hero his own control (stun, root, fear) may hold now; holding it starts its immunity. */
export const holdable = (s: BattleState, h: Hero): boolean => standing(h) && (h.unImmune ?? 0) <= s.tick && (h.frenzy ?? 0) <= 0;
export const held = (s: BattleState, h: Hero): void => {
  h.unImmune = s.tick + UNSLEEPING.heroControlImmune;
};

/** The point within `range` of him with the most standing heroes within `r` of it (a hero's own spot), and how many. */
export const crowd = (s: BattleState, e: Enemy, range: number, r: number, skip: readonly { x: number; y: number }[] = [], apart = 0): { x: number; y: number; n: number } | null => {
  let best: { x: number; y: number; n: number } | null = null;
  for (const h of s.heroes) {
    if (!standing(h) || !withinRange(e.x, e.y, h.x, h.y, range)) continue;
    if (skip.some((p) => withinRange(p.x, p.y, h.x, h.y, apart))) continue;
    const n = heroesNear(s, h.x, h.y, r).length;
    if (!best || n > best.n) best = { x: h.x, y: h.y, n };
  }
  return best;
};

/** The standing hero nearest him (optionally only those `ok` accepts). */
export const nearestHero = (s: BattleState, e: Enemy, range: number, ok: (h: Hero) => boolean = () => true): Hero | null => {
  let best: Hero | null = null;
  let bd = 0;
  for (const h of s.heroes) {
    if (!standing(h) || !ok(h)) continue;
    const dx = h.x - e.x;
    const dy = h.y - e.y;
    const d = dx * dx + dy * dy;
    if (d > range * range) continue;
    if (!best || d < bd) {
      best = h;
      bd = d;
    }
  }
  return best;
};

export const at = (st: Structure): { x: number; y: number } => hexToWorld(st.q, st.r);

/** A wall, a gate, a rampart (towers and buildings also block the way, but aren't walls). */
export const wallLike = (t: StructureType): boolean => STRUCTURES[t].category === 'defense' && STRUCTURES[t].blocks;

/** A standing structure with health that shoots (a tower). */
export const isTower = (st: Structure): boolean => !st.destroyed && st.maxHp > 0 && firesAsTower(st);

/** One of his skills, said for the board: `<id><Name>` at (x, y). */
export const said = (s: BattleState, e: Enemy, u: UnsleepingState, name: string, x = e.x, y = e.y): void => {
  ev(s, { t: 'skill', hero: e.id, skill: `${u.id}${name}`, x, y });
};

/** The Unsleeping out now, if he still stands. */
export const unOut = (s: BattleState): Enemy | undefined => (s.un ? s.enemies.find((e) => e.id === s.un && e.hp > 0 && e.un) : undefined);

/**
 * Marks a blast of his on the ground (an `unBlast` zone): it bursts after `warn` ticks on the heroes within `radius`
 * (`damage` already scaled, at least `hpBp` of each one's health, a stun of `stun` if his control may hold them) and
 * the structures (`towerBp` of their health); the board draws the mark in his colours and his `<id>Blast` burst.
 */
export const markBlast = (s: BattleState, u: UnsleepingState, x: number, y: number, o: { warn: number; radius: number; damage: number; hpBp: number; towerBp?: number; stun?: number }): void => {
  s.zones.push({
    id: s.nextId++, kind: 'unBlast', x, y, radius: o.radius, cells: [], ticks: o.warn + 6, every: 1, damage: mulBp(mulBp(o.damage, u.dmgBp), u.shareBp ?? 10000), slowBp: 0, pid: '', warn: o.warn,
    un: u.id, hpBp: mulBp(o.hpBp, u.shareBp ?? 10000), ...(o.towerBp ? { towerBp: o.towerBp } : {}), ...(o.stun ? { stun: o.stun } : {}),
  });
};

/** Each tick of an `unBlast` zone (updateZones): it counts down, then bursts once. */
export const blastZone = (s: BattleState, z: Zone): void => {
  if ((z.warn ?? 0) <= 0) return;
  z.warn = (z.warn ?? 0) - 1;
  if (z.warn > 0) return;
  z.warn = -1;
  const e = unOut(s);
  for (const h of heroesNear(s, z.x, z.y, z.radius)) {
    damageHero(s, h, by(h, z.damage, z.hpBp ?? 0), e ?? null, undefined, true);
    if (z.stun && holdable(s, h)) {
      h.stun = imax(h.stun, z.stun);
      held(s, h);
    }
  }
  if (z.towerBp) {
    for (const st of s.structures) {
      if (st.destroyed || st.maxHp <= 0) continue;
      const c = at(st);
      if (withinRange(z.x, z.y, c.x, c.y, z.radius)) damageStructure(s, st, imax(1, mulBp(st.maxHp, z.towerBp)), e ?? null);
    }
  }
  ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: `${z.un ?? ''}Blast` });
};
